import { useMemo } from 'react';
import { useGetAuthMe } from '../api/generated/arrayscapeGamingDashboardAPI';
import { useVersionMismatch } from '../components/VersionUpdateNotification';
import { tokenManager } from '../utils/tokenManager';

export { type User } from '../api/generated/model';

/**
 * React Query hook for fetching the current authenticated user.
 *
 * Configuration:
 * - No caching: Always fetches fresh data
 * - Deduplication: Multiple simultaneous calls share the same request
 * - Refetches on window focus to catch auth state changes
 * - Network error handling is done at the global QueryClient level
 * - Stops fetching when version mismatch is detected (dialog showing)
 * - Disabled when no token exists (prevents spam on login page)
 */
export const useAuthUser = () => {
  const versionMismatch = useVersionMismatch();

  // Don't fetch if there's no token - prevents infinite loop on login page
  const hasToken = !!tokenManager.getToken();

  const query = useGetAuthMe({
    query: {
      staleTime: 0,              // Data is immediately stale
      gcTime: 0,                 // Don't keep in cache when component unmounts
      refetchOnMount: versionMismatch ? false : 'always',
      refetchOnWindowFocus: !versionMismatch && hasToken,
      enabled: !versionMismatch && hasToken, // Disable when no token or version mismatch
    },
  });

  const timesheetReports = useMemo(
    () => query.data?.timesheet_reports ?? [],
    [query.data?.timesheet_reports],
  );

  return {
    ...query,
    data: query.data?.user,
    realUser: query.data?.real_user,
    timesheetReports,
  };
};
